/**
 * inlogHerkansing.js — bij drukte één keer automatisch opnieuw inloggen (24-10-2026).
 *
 * Belastingstest 24-10: 300 leerlingen in dezelfde seconde. De inlogdienst (GoTrue)
 * controleert ±20 wachtwoorden per seconde en breekt een verzoek na 10 s af met 504
 * ("Processing this request timed out, please retry after a moment"). 106 van de 300
 * kregen dat. Gespreid over een minuut lukten alle 300.
 *
 * Daarom: bij 502/503/504 (inlogdienst druk of even weg) en 429 (te veel tegelijk)
 * één keer opnieuw na 2 tot 5 seconden. De wachttijd is willekeurig, zodat een klas
 * die samen een 504 kreeg niet opnieuw in dezelfde seconde aanklopt: de piek spreidt
 * zichzelf. Daarna geldt de gewone foutmelding (utils/inlogFout.js).
 * Een fout wachtwoord (400), geen verbinding (0) of een serverfout (500) wordt niet
 * herhaald.
 *
 * Werkt met elke supabase-auth-aanroep die { data, error } teruggeeft
 * (signInWithPassword, verifyOtp). De iPad-app laadt deze pagina's in een webview
 * en krijgt de herkansing dus vanzelf mee.
 */

export const HERKANS_STATUSSEN = [429, 502, 503, 504];
export const MIN_WACHT_MS = 2000;
export const MAX_WACHT_MS = 5000;

export function moetHerkansen(fout) {
  if (!fout) return false;
  return HERKANS_STATUSSEN.includes(Number(fout.status));
}

export function herkansWachttijd(willekeurig = Math.random) {
  return MIN_WACHT_MS + Math.floor(willekeurig() * (MAX_WACHT_MS - MIN_WACHT_MS));
}

const slaap = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Voer een inlogpoging uit; bij drukte nog één keer na een willekeurige wachttijd.
 * @param {() => Promise<{data: any, error: any}>} poging
 * @param {Object} [opties]
 * @param {(ms: number) => Promise<void>} [opties.wacht] voor tests
 * @param {() => number} [opties.willekeurig] voor tests
 * @param {(ms: number) => void} [opties.bijHerkansing] om de gebruiker te laten weten dat we het opnieuw proberen
 */
export async function metHerkansing(poging, { wacht = slaap, willekeurig = Math.random, bijHerkansing } = {}) {
  const eerste = await poging();
  if (!moetHerkansen(eerste && eerste.error)) return eerste;
  const ms = herkansWachttijd(willekeurig);
  if (bijHerkansing) bijHerkansing(ms);
  await wacht(ms);
  return poging();
}
